import { apiClient, ApiClient } from "@/lib/api/api-client";
import { createStorageService } from "@/lib/storage/gcs/googleCloudStorage";
import { IStorageService } from "@/lib/storage/storage";
import {
  EmployeeInfo,
  EmployeeInfoRegister,
  EmployeeInfoUpdate,
  EmployeeInfoUpdateIntroduce,
  EmployeeFullInfo,
} from "@/modules/types/employee/employee";
import { BaseResponse } from "@/modules/types/response";
import {
  Degree,
  DegreeCreate,
  DegreeUpdate,
} from "../types/employee/education";
import {
  Experience,
  ExperienceCreate,
  ExperienceUpdate,
} from "../types/employee/experience";
import {
  Certificate,
  CertificateCreate,
  CertificateUpdate,
} from "../types/employee/certificate";
import { Course, CourseCreate, CourseUpdate } from "../types/employee/course";
import {
  PortfolioProject,
  PortfolioProjectCreate,
  PortfolioProjectUpdate,
} from "../types/employee/portfolioProject";
import { EmployeeDashboardResponse } from "../types/employee/dashboard";

interface EmployeeInfoRegisterResp extends BaseResponse, EmployeeInfoRegister { }

interface EmployeeResp extends BaseResponse {
  employeeInfo: EmployeeInfo;
}

interface AIGenSuggestionForJobResp extends BaseResponse {
  suggestion: string;
}

export interface GetSharedEmployeeProfileResp extends BaseResponse {
  employeeInfo?: EmployeeFullInfo;
}

export interface IsMeResponse extends BaseResponse {
  result: boolean;
}

export interface SubmitInterviewReviewRequest {
  sessionId: string;
  rate: number;
  reviewerName: string;
  comment: string;
}

interface GetDegreesResp extends BaseResponse {
  employeeDegree: Degree[];
}

interface CreateDegreeResp extends BaseResponse {
  employeeDegree: Degree;
}

interface GetExperiencesResp extends BaseResponse {
  experiences: Experience[];
}

interface CreateExperienceResp extends BaseResponse {
  experience: Experience;
}

interface GetCertificatesResp extends BaseResponse {
  employeeCertificates: Certificate[];
}

interface CreateCertificateResp extends BaseResponse {
  employeeCertificate: Certificate;
}

interface GetCoursesResp extends BaseResponse {
  employeeCourses: Course[];
}

interface CreateCourseResp extends BaseResponse {
  employeeCourse: Course;
}

interface GetPortfolioProjectsResp extends BaseResponse {
  portfolioProjects: PortfolioProject[];
}

interface CreatePortfolioProjectResp extends BaseResponse {
  portfolioProject: PortfolioProject;
}

export class EmployeeService {
  private BASE_PATH =
    process.env.API_EMPLOYEEE_ENDPOINT || "http://localhost:8081/employee";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  private storage: IStorageService = createStorageService();
  private destination = "employee/";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async registerInfo(
    info: EmployeeInfoRegister,
  ): Promise<EmployeeInfoRegisterResp> {
    return this.apiClient.fetch<EmployeeInfoRegisterResp>(
      `${this.BASE_PATH}/${this.apiVersion}/candidate/regist`,
      {
        method: "POST",
        body: JSON.stringify(info),
      },
    );
  }

  async getByDefault(): Promise<EmployeeResp | undefined> {
    return this.apiClient.fetch<EmployeeResp>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
    );
  }

  async getSharedEmployeeProfile(
    sessionId: string,
  ): Promise<GetSharedEmployeeProfileResp> {
    return this.apiClient.fetch<GetSharedEmployeeProfileResp>(
      `${this.BASE_PATH}/${this.apiVersion}/shared-profile/${sessionId}`,
    );
  }

  async submitInterviewReview(
    data: SubmitInterviewReviewRequest,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/interview-review`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  async getDashboard(): Promise<EmployeeDashboardResponse> {
    return this.apiClient.fetch<EmployeeDashboardResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/dashboard`,
    );
  }

  async aiGenSuggestionForJob(
    employeeId: string,
    jobId: string,
  ): Promise<AIGenSuggestionForJobResp> {
    return this.apiClient.fetch<AIGenSuggestionForJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/suggestion/job-requirement/${employeeId}/${jobId}`,
    );
  }

  async uploadAvatar(
    employeeId: string,
    file: File,
    fileName: string,
  ): Promise<string> {
    const filePath = `${this.destination}${employeeId}/avatar/${fileName}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async updateAvatar(
    employeeId: string,
    avatar: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
      {
        method: "PUT",
        body: JSON.stringify({ id: employeeId, avatar }),
      },
    );
  }

  async update(
    employeeId: string,
    employeeInfo: EmployeeInfoUpdate | EmployeeInfoUpdateIntroduce,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/information`,
      {
        method: "PUT",
        body: JSON.stringify({ ...employeeInfo, id: employeeId }),
      },
    );
  }

  async getDegrees(employeeId: string): Promise<GetDegreesResp> {
    return this.apiClient.fetch<GetDegreesResp>(
      `${this.BASE_PATH}/${this.apiVersion}/degrees?employeeId=${employeeId}`,
    );
  }

  async createDegree(
    employeeId: string,
    degree: DegreeCreate,
  ): Promise<CreateDegreeResp> {
    return this.apiClient.fetch<CreateDegreeResp>(
      `${this.BASE_PATH}/${this.apiVersion}/degree`,
      {
        method: "POST",
        body: JSON.stringify({ ...degree, employeeId }),
      },
    );
  }

  async updateDegree(
    employeeId: string,
    degree: DegreeUpdate,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/degree/${degree.id}`,
      {
        method: "PUT",
        body: JSON.stringify({ ...degree, employeeId }),
      },
    );
  }

  async deleteDegree(
    employeeId: string,
    degreeId: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/degree/${employeeId}/${degreeId}`,
      {
        method: "DELETE",
      },
    );
  }

  async getExperiences(employeeId: string): Promise<GetExperiencesResp> {
    return this.apiClient.fetch<GetExperiencesResp>(
      `${this.BASE_PATH}/${this.apiVersion}/experiences?employeeId=${employeeId}`,
    );
  }

  async createExperience(
    employeeId: string,
    experience: ExperienceCreate,
  ): Promise<CreateExperienceResp> {
    return this.apiClient.fetch<CreateExperienceResp>(
      `${this.BASE_PATH}/${this.apiVersion}/experience`,
      {
        method: "POST",
        body: JSON.stringify({ ...experience, employeeId }),
      },
    );
  }

  async updateExperience(
    employeeId: string,
    experience: ExperienceUpdate,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/experience/${experience.id}`,
      {
        method: "PUT",
        body: JSON.stringify({ ...experience, employeeId }),
      },
    );
  }

  async deleteExperience(
    employeeId: string,
    experienceId: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/experience/${employeeId}/${experienceId}`,
      {
        method: "DELETE",
      },
    );
  }

  async uploadBusinessLogo(employeeId: string, file: File): Promise<string> {
    const filePath = `${this.destination}${employeeId}/business-logo/${file.name}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async uploadCertificate(employeeId: string, file: File): Promise<string> {
    const filePath = `${this.destination}${employeeId}/certificates/${file.name}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async uploadVideo(employeeId: string, file: File): Promise<string> {
    const filePath = `${this.destination}${employeeId}/videos/${file.name}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async getCertificates(employeeId: string): Promise<GetCertificatesResp> {
    return this.apiClient.fetch<GetCertificatesResp>(
      `${this.BASE_PATH}/${this.apiVersion}/certificates?employeeId=${employeeId}`,
    );
  }

  async createCertificate(
    employeeId: string,
    certificate: CertificateCreate,
  ): Promise<CreateCertificateResp> {
    return this.apiClient.fetch<CreateCertificateResp>(
      `${this.BASE_PATH}/${this.apiVersion}/certificate`,
      {
        method: "POST",
        body: JSON.stringify({ ...certificate, employeeId }),
      },
    );
  }

  async updateCertificate(
    employeeId: string,
    certificate: CertificateUpdate,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/certificate/${employeeId}/${certificate.id}`,
      {
        method: "PUT",
        body: JSON.stringify(certificate),
      },
    );
  }

  async deleteCertificate(
    employeeId: string,
    certificateId: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/certificate/${employeeId}/${certificateId}`,
      {
        method: "DELETE",
      },
    );
  }

  async getCourses(employeeId: string): Promise<GetCoursesResp> {
    return this.apiClient.fetch<GetCoursesResp>(
      `${this.BASE_PATH}/${this.apiVersion}/courses?employeeId=${employeeId}`,
    );
  }

  async createCourse(
    employeeId: string,
    course: CourseCreate,
  ): Promise<CreateCourseResp> {
    return this.apiClient.fetch<CreateCourseResp>(
      `${this.BASE_PATH}/${this.apiVersion}/course`,
      {
        method: "POST",
        body: JSON.stringify({ ...course, employeeId }),
      },
    );
  }

  async updateCourse(
    employeeId: string,
    course: CourseUpdate,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/course/${employeeId}/${course.id}`,
      {
        method: "PUT",
        body: JSON.stringify(course),
      },
    );
  }

  async deleteCourse(
    employeeId: string,
    courseId: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/course/${employeeId}/${courseId}`,
      {
        method: "DELETE",
      },
    );
  }

  async getPortfolioProjects(
    employeeId: string,
  ): Promise<GetPortfolioProjectsResp> {
    return this.apiClient.fetch<GetPortfolioProjectsResp>(
      `${this.BASE_PATH}/${this.apiVersion}/portfolio-projects?employeeId=${employeeId}`,
    );
  }

  async createPortfolioProject(
    employeeId: string,
    portfolioProject: PortfolioProjectCreate,
  ): Promise<CreatePortfolioProjectResp> {
    return this.apiClient.fetch<CreatePortfolioProjectResp>(
      `${this.BASE_PATH}/${this.apiVersion}/portfolio-project`,
      {
        method: "POST",
        body: JSON.stringify({ ...portfolioProject, employeeId }),
      },
    );
  }

  async updatePortfolioProject(
    employeeId: string,
    portfolioProject: PortfolioProjectUpdate,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/portfolio-project/${portfolioProject.id}`,
      {
        method: "PUT",
        body: JSON.stringify({ ...portfolioProject, employeeId }),
      },
    );
  }

  async deletePortfolioProject(
    employeeId: string,
    portfolioProjectId: string,
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/portfolio-project/${employeeId}/${portfolioProjectId}`,
      {
        method: "DELETE",
      },
    );
  }

  async uploadPortfolioProjectThumbnail(
    employeeId: string,
    file: File | Blob,
  ): Promise<string> {
    let extension = "jpg";
    if (file instanceof File) {
      const match = file.name.match(/\.([a-zA-Z0-9]+)$/);
      if (match) extension = match[1];
    } else if (file.type) {
      const typeMatch = file.type.match(/image\/(.+)/);
      if (typeMatch) extension = typeMatch[1];
    }
    const fileName = `${Date.now()}-${Math.random()
      .toString(36)
      .substring(7)}.${extension}`;
    const filePath = `employee/${employeeId}/portfolio-project/thumbnails/${fileName}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async uploadPortfolioProjectContentFile(
    employeeId: string,
    file: File | Blob,
    type: "images" | "files" | "audio",
    originalFileName?: string,
  ): Promise<string> {
    let fileName: string;
    if (type === "files" && originalFileName) {
      fileName = originalFileName;
    } else {
      let extension = "jpg";
      if (type === "audio") {
        extension = "mp3";
        if (file instanceof File) {
          const match = file.name.match(/\.([a-zA-Z0-9]+)$/);
          if (match) extension = match[1];
        } else if (file.type) {
          const typeMatch = file.type.match(/audio\/(.+)/);
          if (typeMatch) extension = typeMatch[1];
        }
      } else if (type === "images") {
        if (file instanceof File) {
          const match = file.name.match(/\.([a-zA-Z0-9]+)$/);
          if (match) extension = match[1];
        } else if (file.type) {
          const typeMatch = file.type.match(/image\/(.+)/);
          if (typeMatch) extension = typeMatch[1];
        }
      }
      fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(7)}.${extension}`;
    }
    const filePath = `employee/${employeeId}/portfolio-project/${type}/${fileName}`;
    return this.storage.uploadPublicFile(file, filePath);
  }

  async isMe(employeeId: string): Promise<IsMeResponse> {
    return this.apiClient.fetch<IsMeResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/is_me/${employeeId}`,
    );
  }
}

export const employeeService = new EmployeeService();
